import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, MailX, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/parts-catalog/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Email preferences | Rex & Sons RV Parts" },
      { name: "description", content: "Manage email contact preferences for the Rex & Sons RV parts store." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Email preferences | Rex & Sons RV Parts" },
      { property: "og:description", content: "Manage the reminder emails you receive from the Rex & Sons RV parts store." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartsUnsubscribe,
});

type State =
  | { kind: "loading" }
  | { kind: "ready"; email: string }
  | { kind: "already"; email: string }
  | { kind: "done"; email: string }
  | { kind: "error"; message: string };

function PartsUnsubscribe() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
    if (!t) {
      setState({ kind: "error", message: "This unsubscribe link is missing its code." });
      return;
    }
    fetch(`/api/public/parts-email-unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.error) setState({ kind: "error", message: d.error });
        else if (d?.already) setState({ kind: "already", email: d.email });
        else setState({ kind: "ready", email: d.email });
      })
      .catch(() => setState({ kind: "error", message: "We couldn't check that link. Please try again." }));
  }, []);

  async function confirm() {
    setBusy(true);
    try {
      const res = await fetch("/api/public/parts-email-unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      if (d?.error) setState({ kind: "error", message: d.error });
      else setState({ kind: "done", email: d.email });
    } catch {
      setState({ kind: "error", message: "We couldn't update your preferences. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-16 max-w-lg">
      <div className="border rounded-lg p-8 bg-card text-center">
        {state.kind === "loading" && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your link…
          </div>
        )}

        {state.kind === "ready" && (
          <>
            <MailX className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
             <h1 className="text-2xl font-bold mb-2">Stop Parts &amp; Gear emails?</h1>
            <p className="text-muted-foreground mb-6">
               We'll stop sending Parts &amp; Gear emails to {state.email}. You can turn them back on anytime in My Account.
            </p>
            <Button onClick={confirm} disabled={busy}>
              {busy ? "Saving…" : "Yes, unsubscribe me"}
            </Button>
          </>
        )}

        {(state.kind === "done" || state.kind === "already") && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto mb-4 text-primary" />
            <h1 className="text-2xl font-bold mb-2">You're unsubscribed</h1>
            <p className="text-muted-foreground mb-6">
               {state.email} will no longer receive Parts &amp; Gear emails from us.
            </p>
            <Button asChild variant="outline"><Link to="/parts-catalog">Back to the parts store</Link></Button>
          </>
        )}

        {state.kind === "error" && (
          <>
            <h1 className="text-2xl font-bold mb-2">We couldn't do that</h1>
            <p className="text-muted-foreground mb-6">{state.message}</p>
            <Button asChild variant="outline"><Link to="/parts-catalog">Back to the parts store</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
